'use client'

import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect } from 'react'
import useAccessTokenReader from '@/utils/auth/useAccessTokenReader'
import { readCompanyFromLocalStorage } from '@/utils/useCompanyFromLocalStorage'

import { useUserStore } from '../../stores/user-store'

/**
 * Hook that automatically loads company data when companyId changes
 */
export const useTokenLoader = () => {
  const {
    user: auth0User,
    isLoading: auth0IsLoading,
    error: auth0Error,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0()

  const {
    getAllRoles,
    isAdminFunc,
    isDashboardUserFunc,
    isDemoUserFunc,
    isLeadUserFunc,
    getCompanyId,
    getDefaultResultId,
  } = useAccessTokenReader()

  const appStateSetUser = useUserStore((state) => state.setUser)
  const appStateSetIsAdmin = useUserStore((state) => state.setIsAdmin)
  const appStateSetIsDashboardUser = useUserStore((state) => state.setIsDashboardUser)
  const appStateSetDemoLogin = useUserStore((state) => state.setIsDemoUser)
  const appStateSetLeadUser = useUserStore((state) => state.setIsLeadUser)
  const appStateSetUserRoles = useUserStore((state) => state.setUserRoles)
  const appStateSetCompanyId = useUserStore((state) => state.setCompanyId)
  const appStateSetDefaultResultId = useUserStore((state) => state.setDefaultResultId)
  const appStateSetLoading = useUserStore((state) => state.setLoading)

  const afterTokenLoad = useCallback(
    (newToken: string) => {
      console.log('[useTokenLoader] Access token:')
      console.log(newToken)

      appStateSetUser(auth0User)

      const allRoles = getAllRoles(auth0User)
      appStateSetUserRoles(allRoles)

      const isAdmin = isAdminFunc(auth0User)
      appStateSetIsAdmin(isAdmin)

      const isDashboardUser = isDashboardUserFunc(auth0User)
      appStateSetIsDashboardUser(isDashboardUser)

      const isDemoUser = isDemoUserFunc(auth0User)
      appStateSetDemoLogin(isDemoUser)

      const isLeadUser = isLeadUserFunc(auth0User)
      appStateSetLeadUser(isLeadUser)

      // Prefer companyId from localStorage (for admin), fallback to token
      const companyIdFromLocalStorage = readCompanyFromLocalStorage(auth0User)
      const companyIdFromToken = getCompanyId(auth0User)
      const companyId = companyIdFromLocalStorage ?? companyIdFromToken
      appStateSetCompanyId(companyId)

      const defaultResultId = getDefaultResultId(auth0User)
      appStateSetDefaultResultId(defaultResultId)

      appStateSetLoading(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth0User],
  )

  const onNotLoggedIn = useCallback(
    (error: any) => {
      appStateSetUser(undefined)
      appStateSetIsAdmin(false)
      appStateSetIsDashboardUser(false)
      appStateSetUserRoles([])
      appStateSetDemoLogin(false)
      appStateSetCompanyId(undefined)
      appStateSetDefaultResultId(undefined)

      // Mix of concerns here. The CheckAuthorization component should handle the redirect to login depending on the URL,
      // but we also want to stop the loading state.
      // loginWithRedirect().catch((loginError) => {
      //   console.error('[useTokenLoader] Error during login redirect:', loginError)
      //   appStateSetLoading(false)
      // })
      console.warn('[useTokenLoader] User is not logged in. Set app state accordingly.')

      if (error) {
        console.warn('[useTokenLoader] The auth token load has failed:', error)
      }

      appStateSetLoading(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loginWithRedirect],
  )

  useEffect(() => {
    let cancelled = false

    if (auth0IsLoading) {
      console.log('[useTokenLoader] Auth0 is loading...')
      appStateSetLoading(true)
      return
    } else if (auth0Error) {
      console.error('[useTokenLoader] Auth0 error:', auth0Error)
      onNotLoggedIn(auth0Error)
      return
    } else {
      console.log('[useTokenLoader] Auth0 loading done. Now get token ....')
      appStateSetLoading(true)
      getAccessTokenSilently()
        .then((token) => {
          if (!cancelled) afterTokenLoad(token)
        })
        .catch((error) => {
          if (!cancelled) onNotLoggedIn(error)
        })
    }

    return () => {
      cancelled = true
    }
  }, [auth0IsLoading, auth0Error, afterTokenLoad, getAccessTokenSilently, onNotLoggedIn, appStateSetLoading])

  return {}
}
